import { AccountId, AssetType, Offering, OfferingId } from '@arrived/models';

import { EVENTS } from './Events';

export enum EventSource {
  MOBILE = 'mobile',
  WEB = 'web',
}

export type EventProperties = {
  [EVENTS.DOCUMENTS_SIGNED]: DocumentSignedEventProperties;
  [EVENTS.ONBOARDING_STEP_COMPLETED]: OnboardingCompletedEventProperties;
  [EVENTS.CTA_CLICKED]: CTAClickedEventProperties;
  [EVENTS.ACCOUNT_CREATED]: AccountCreateEventProperties;
  [EVENTS.PROPERTY_VIEWED]: PropertyViewedEventProperties;
  [EVENTS.PORTFOLIO_PAGE_VIEWED]: PortfolioPageViewedEventProperties;
  [EVENTS.BROKERAGE_ACCOUNT_CREATED]: BrokerageAccountCreatedEventProperties;
  [EVENTS.OFFERING_FEATURED_FILTER_CLICKED]: OfferingFeaturedFilterClickedEventProperties;
  [EVENTS.OFFERING_FILTER_CLICKED]: OfferingFilterClickedEventProperties;
  [EVENTS.BANK_LINK]: BankLinkEventProperties;
  [EVENTS.PLAID_BANK_LINKED]: PlaidBankLinkEventProperties;
  [EVENTS.EMAIL_SUBSCRIPTION]: EmailSubscriptionEventProperties;
  [EVENTS.EXPERIMENT_VIEWED]: ExperimentViewedEventProperties;
  [EVENTS.OFFERING_VIDEO_WATCHED]: OfferingVideoWatchedEventProperties;
  [EVENTS.LEAD_FORM_COMPLETED]: LeadFormCompleted;
  [EVENTS.DEEP_LINK]: DeepLinkEventProperties;
  [EVENTS.NOT_FOUND]: NotFoundEventProperties;
  [EVENTS.SIGNUP_FORM_DISPLAYED]: SignupFormDisplayedEventProperties;
  [EVENTS.ONBOARDING_STEP_COMPLETED_MOBILE]: OnboardingCompletedEventProperties;
};

export interface NotFoundEventProperties {
  key?: string;
  path: string;
  name: string;
}

export interface DeepLinkEventProperties {
  url: string;
}

export interface LeadFormCompleted {
  email: string;
  first_name?: string;
  last_name?: string;
  // Unbounce, demio, app landing page, etc.
  lead_form_type: string;
  url: string;
  referral_source: string;
}

export interface OfferingVideoWatchedEventProperties {
  offering_id: OfferingId;
  property_name: Offering['name'];
}

export interface DocumentSignedEventProperties {
  id: number;
  asset_type: AssetType;
  total_transaction_amount: number;
  revenue: number;
  currency: string;
  property_status: string;
  num_shares_purchased: number;
  property_tag_status: string;
  beds: number;
  baths: number;
  sq_ft: number;
  year_built: number;
  number_of_investors: number;
  property_purchase_price: number;
  monthly_rent: number;
  offering_id: number;
  property_name: string;
  perc_purchased_status: number;
  property_rental_status: string;
  first_dividend_date: string;
  first_dividend_yield: number;
  total_historical_return_min: number;
  total_historical_return_max: number;
  historical_rental_dividends: number;
  property_value_growth: number;
  signing_completion_location: string;
}

interface OnboardingCompletedEventProperties {
  email: string;
  first_name: string;
  last_name: string;
  citizenship: string;
  brokerage_account_type: string;
  address_account: string;
  city_account: string;
  state_account: string;
  zip_account: string;
  date_of_birth: string;
  accredited_selection: string;
  step: string;
}

interface CTAClickedEventProperties {
  destination: string;
  location: string;
  text: string;
  type: string;
  url: string;
  short_name?: string;
  offering_id?: number;
}

export enum SignupFormComponent {
  SOLD_OUT_POP_UP = 'sold-out-pop-up',
  OFFERING_VIEW_LIMIT_POP_UP = 'offering-view-limit-pop-up',
}

export interface SignupFormDisplayedEventProperties {
  component: SignupFormComponent;
  action: string;
}

export interface AccountCreateEventProperties {
  email: string;
  first_name: string;
  last_name: string;
  intended_investment_amount: string;
  how_did_you_hear: string;
  page: string;
  component: string;
}

interface PropertyViewedEventProperties {
  asset_type: AssetType;
  property_status: string;
  beds: number;
  baths: number;
  sq_ft: number;
  year_built: number;
  amount_invested: number;
  number_of_investors: number;
  property_purchase_price: number;
  monthly_rent: number;
  offering_id: number;
  offering_name: string;
  perc_purchased_status: number;
  property_rental_status: string;
  first_dividend_date: string;
  first_dividend_yield: number;
  total_historical_return_min: number;
  total_historical_return_max: number;
  historical_rental_dividends: number;
  property_value_growth: number;
}

interface PortfolioPageViewedEventProperties {
  initial_investment: number;
  total_balance: number;
  total_returns: number;
  total_appreciation: number;
  total_dividends: number;
  number_current_properties_owned: number;
  list_current_properties_owned: string;
}

interface BrokerageAccountCreatedEventProperties {
  email: string;
  account_type: string;
}

interface OfferingFeaturedFilterClickedEventProperties {
  feature_name: string;
}

interface OfferingFilterClickedEventProperties {
  filter_name: string;
}

interface EmailSubscriptionEventProperties {
  position: string;
}

export enum BankLinkEventType {
  CREATE = 'create',
  VERIFY = 'verify',
  DELETE = 'delete',
}

interface BankLinkEventProperties {
  update_type: BankLinkEventType;
  connection_type: string;
  verification_status: string;
}

interface PlaidBankLinkEventProperties {
  accountId: AccountId;
}

interface ExperimentViewedEventProperties {
  experimentName: string;
  /**
   * Use this for tracking the experimental group in a basic A/B test.
   */
  experimentGroup?: boolean;
  /**
   * Use this for tracking groups in multi-variate tests.
   */
  experimentGroupNumber?: number;
}
